import { useCallback, useEffect, useRef } from "react";
import type { MatrixClient } from "matrix-js-sdk";

const TYPING_TIMEOUT_MS = 4000;

/**
 * Returns a `notifyTyping` callback to call on each keystroke. It sends a
 * single `m.typing` start, refreshes the server timeout, and auto-sends a
 * stop after inactivity. Sends a final stop on unmount / room change.
 */
export function useTypingSender(client: MatrixClient, roomId: string): () => void {
  const isTypingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stop = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    if (isTypingRef.current) {
      isTypingRef.current = false;
      client.sendTyping(roomId, false, 0).catch(() => undefined);
    }
  }, [client, roomId]);

  const notifyTyping = useCallback(() => {
    if (!isTypingRef.current) {
      isTypingRef.current = true;
      client.sendTyping(roomId, true, TYPING_TIMEOUT_MS).catch(() => undefined);
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(stop, TYPING_TIMEOUT_MS);
  }, [client, roomId, stop]);

  useEffect(() => stop, [stop]);

  return notifyTyping;
}
